import moment from 'moment';
import 'moment/locale/fr';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SideBarAdmin from '../../components/SideBarAdmin';
import { useAxios } from '../../utils/hook/useAxios';

moment.locale('fr');

const PAGE_SIZE = 20;

const STATUS_MAP = {
  pending: {
    label: 'En attente',
    bg: 'bg-warning-subtle  text-warning',
    icon: 'bi-clock-history',
  },
  confirmed: {
    label: 'Confirmée',
    bg: 'bg-success-subtle  text-success',
    icon: 'bi-check-circle-fill',
  },
  completed: {
    label: 'Terminée',
    bg: 'bg-primary-subtle  text-primary',
    icon: 'bi-flag-fill',
  },
  cancelled: {
    label: 'Annulée',
    bg: 'bg-danger-subtle   text-danger',
    icon: 'bi-x-circle-fill',
  },
  paid: {
    label: 'Payée',
    bg: 'bg-success-subtle  text-success',
    icon: 'bi-credit-card-fill',
  },
  refused: {
    label: 'Refusée',
    bg: 'bg-danger-subtle   text-danger',
    icon: 'bi-slash-circle-fill',
  },
};

function StatusBadge({ status }) {
  const s = STATUS_MAP[status] ?? {
    label: status ?? '—',
    bg: 'bg-secondary-subtle text-secondary',
    icon: 'bi-circle',
  };
  return (
    <span className={`badge rounded-pill ${s.bg}`} style={{ fontSize: 11 }}>
      <i className={`bi ${s.icon} me-1`}></i>
      {s.label}
    </span>
  );
}

function AdminReservations() {
  const [reservations, setReservations] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [seedResult, setSeedResult] = useState(null);
  const axios = useAxios();

  const handleSeed = async () => {
    setSeeding(true);
    setSeedResult(null);
    try {
      const { data } = await axios.post('/seed/reservations');
      setSeedResult({ success: true, count: data.created });
      // Recharger la liste
      const { data: list } = await axios.get('/reservations');
      if (Array.isArray(list)) {
        setReservations(list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
      }
    } catch (err) {
      setSeedResult({
        success: false,
        error: err.response?.data?.error || err.message,
      });
    } finally {
      setSeeding(false);
    }
  };

  useEffect(() => {
    setIsLoading(true);
    axios
      .get('/reservations')
      .then(({ data }) => {
        if (Array.isArray(data)) {
          setReservations(data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
        }
      })
      .catch((err) => console.error('Erreur Axios :', err))
      .finally(() => setIsLoading(false));
  }, []);

  /* Reset page when search changes */
  const handleSearch = (val) => {
    setSearch(val);
    setPage(1);
  };

  const filtered = reservations.filter((r) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      String(r.reservationId).includes(q) ||
      r.status?.toLowerCase().includes(q) ||
      r.Host?.email?.toLowerCase().includes(q) ||
      r.Host?.firstName?.toLowerCase().includes(q) ||
      r.Host?.lastName?.toLowerCase().includes(q) ||
      r.Invite?.email?.toLowerCase().includes(q) ||
      r.Invite?.firstName?.toLowerCase().includes(q) ||
      r.PaymentIntentId?.toLowerCase().includes(q)
    );
  });

  const visible = filtered.slice(0, page * PAGE_SIZE);
  const hasMore = visible.length < filtered.length;

  return (
    <section className="bg-body-tertiary min-vh-100">
      <div className="container py-3 pb-5">
        <div className="row g-3">
          {/* Sidebar */}
          <div className="col-md-3 d-none d-md-block">
            <SideBarAdmin />
          </div>

          <div className="col-md-9">
            {/* Header */}
            <div className="d-flex align-items-center gap-2 mb-3">
              <h5 className="mb-0 fw-bold flex-grow-1">
                <i className="bi bi-calendar2-check-fill me-2 text-primary"></i>
                Réservations
              </h5>
              <span className="badge bg-secondary-subtle text-secondary rounded-pill px-3">
                {filtered.length} résultat{filtered.length !== 1 ? 's' : ''}
              </span>
              <button
                className="btn btn-sm btn-outline-warning"
                onClick={handleSeed}
                disabled={seeding}
                title="Génère des réservations fictives avec les vrais hôtes/véhicules de la base"
              >
                {seeding ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1" />
                    Génération…
                  </>
                ) : (
                  <>
                    <i className="bi bi-database-fill-add me-1" />
                    Générer données test
                  </>
                )}
              </button>
            </div>
            {seedResult && (
              <div
                className={`alert alert-sm py-2 ${seedResult.success ? 'alert-success' : 'alert-danger'} mb-3`}
              >
                {seedResult.success ? (
                  <>
                    <i className="bi bi-check-circle me-1" />
                    {seedResult.count} réservation(s) créée(s)
                  </>
                ) : (
                  <>
                    <i className="bi bi-exclamation-triangle me-1" />
                    {seedResult.error}
                  </>
                )}
              </div>
            )}

            {/* Search */}
            <div className="mb-3">
              <div className="input-group">
                <span className="input-group-text bg-white">
                  <i className="bi bi-search text-muted"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Rechercher par ID, statut, email hôte / locataire…"
                  value={search}
                  onChange={(e) => handleSearch(e.target.value)}
                />
                {search && (
                  <button className="btn btn-outline-secondary" onClick={() => handleSearch('')}>
                    <i className="bi bi-x"></i>
                  </button>
                )}
              </div>
            </div>

            {/* Table */}
            <div className="mt-2 card border-0 shadow-sm rounded-4 overflow-hidden w-100">
              {isLoading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" />
                </div>
              ) : filtered.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <i className="bi bi-inbox" style={{ fontSize: 36 }}></i>
                  <p className="mt-2 mb-0">Aucune réservation trouvée</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover mb-0 align-middle">
                    <thead className="table-light">
                      <tr>
                        <th style={{ fontSize: 12 }}>Date</th>
                        <th style={{ fontSize: 12 }}>Réservation</th>
                        <th style={{ fontSize: 12 }} className="d-none d-sm-table-cell">
                          Hôte → Locataire
                        </th>
                        <th style={{ fontSize: 12 }}>Statut</th>
                        <th style={{ fontSize: 12 }} className="d-none d-md-table-cell">
                          Dates
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {visible.map((item) => (
                        <tr key={item.reservationId}>
                          <td style={{ fontSize: 12 }} className="text-muted">
                            {moment(item.createdAt).format('DD/MM/YY')}
                          </td>
                          <td>
                            <Link
                              to={`/admin/reservation/${item.reservationId}`}
                              className="fw-semibold text-decoration-none"
                              style={{ fontSize: 13 }}
                            >
                              #{item.reservationId}
                            </Link>
                          </td>
                          <td className="d-none d-sm-table-cell" style={{ fontSize: 12 }}>
                            <span
                              className="text-truncate d-inline-block"
                              style={{ maxWidth: 140 }}
                            >
                              <i
                                className="bi bi-house text-primary me-1"
                                style={{ fontSize: 10 }}
                              ></i>
                              {item.Host?.firstName} {item.Host?.lastName}
                            </span>
                            <i
                              className="bi bi-arrow-right text-muted mx-1"
                              style={{ fontSize: 10 }}
                            ></i>
                            <span
                              className="text-truncate d-inline-block"
                              style={{ maxWidth: 140 }}
                            >
                              <i
                                className="bi bi-person text-success me-1"
                                style={{ fontSize: 10 }}
                              ></i>
                              {item.Invite?.firstName} {item.Invite?.lastName}
                            </span>
                          </td>
                          <td>
                            <StatusBadge status={item.status} />
                          </td>
                          <td
                            className="d-none d-md-table-cell text-muted"
                            style={{ fontSize: 11 }}
                          >
                            {item.startDate && item.endDate
                              ? `${moment(item.startDate).format('DD MMM')} → ${moment(item.endDate).format('DD MMM YY')}`
                              : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Pagination */}
            {hasMore && (
              <div className="text-center mt-3">
                <button
                  className="btn btn-outline-primary rounded-pill px-4"
                  onClick={() => setPage((p) => p + 1)}
                >
                  <i className="bi bi-chevron-down me-2"></i>
                  Charger plus ({filtered.length - visible.length} restant
                  {filtered.length - visible.length > 1 ? 's' : ''})
                </button>
              </div>
            )}

            {!isLoading && filtered.length > 0 && (
              <p className="text-muted text-center mt-2 mb-0" style={{ fontSize: 12 }}>
                {visible.length} / {filtered.length} réservation
                {filtered.length > 1 ? 's' : ''}
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export default AdminReservations;
